import { FEATURE_ROUTES } from "../../config/navigation";
import SidebarFlyout from "./sidebar-flyout";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Image, Badge } from "uikit-react";
import UIkit from "uikit";
import { Link as RawLink } from "../ui-kit/Link";
import { AppContexts } from "../../providers";
import { usePermissionsStore } from "../permissions/permissions_store";
import FEATURES from "../../config/features";
import feather from "feather-icons";
import {
  getActiveSidebarMenu,
  GUEST_GUIDE_PATHS,
  matchesSidebarPath,
  MESSAGING_PATHS,
  toggleSidebarMenu,
} from "./sidebar_state";

// Helper component to render Feather icons
const FeatherIcon = ({ name, className = "" }) => {
  const icon = feather.icons[name];
  if (!icon) return null;
  
  return (
    <svg
      className={`feather-icon ${className}`}
      viewBox="0 0 24 24"
      dangerouslySetInnerHTML={{ __html: icon.contents }}
      style={{ color: "var(--dark-purple)" }}
    />
  );
};

// Pin icon with active (upright) and inactive (tilted) states
const PinIcon = ({ active }) => (
  <svg
    className="feather-icon pin-icon"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      color: active ? "var(--dark-purple)" : "currentColor",
      transform: active ? "none" : "rotate(45deg)",
      transition: "transform 0.2s ease, color 0.2s ease",
    }}
  >
    <path
      d="M12 2v8l4 2v3H8v-3l4-2V2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 15h8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M12 15v7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const Link = ({ onClick, ...props }) => (
  <RawLink
    onClick={() => {
      onClick && onClick();
    }}
    {...props}
  />
);

// Returns a predicate that decides whether a nav entry should be visible for
// the current user. Accepts a single module key or an array (show if ANY is
// viewable). Permission-gated items stay hidden until permissions finish
// loading so unauthorized menus never flash on initial login.
const useCanShow = () => {
  const { canView, permsLoaded } = usePermissionsStore();
  return useCallback(
    (mod) => {
      if (!mod) return true;
      if (!permsLoaded) return false;
      if (Array.isArray(mod)) return mod.some((m) => canView(m));
      return canView(mod);
    },
    [canView, permsLoaded],
  );
};

const SidebarItem = ({
  to = "#",
  label,
  icon,
  avatarSrc,
  className = "",
  collapsed,
  onClick,
  badge,
  children,
  isOpen,
  onToggle,
  isFlyoutOpen,
  onFlyoutToggle,
  moduleKey,
  requireAny,
}) => {
  const canShow = useCanShow();
  const hasChildren = !!children;
  const anchorRef = useRef(null);
  const flyoutId = `sidebar-flyout-${label.toLowerCase().replace(/\s+/g, "-")}`;

  if (!canShow(requireAny || moduleKey)) return null;

  const leftContent = (
    <span className="sidebar-link-left">
      {avatarSrc ? (
        <span className="sidebar-icon-wrap">
          <img src={avatarSrc} className="sidebar-avatar" alt={label} />
        </span>
      ) : icon ? (
        <span className="sidebar-icon-wrap">
          <FeatherIcon name={icon} />
        </span>
      ) : null}

      {!collapsed && <span className="sidebar-label">{label}</span>}
    </span>
  );

  if (hasChildren) {
    if (collapsed) {
      return (
        <li className={`sidebar-item has-children ${className} ${isFlyoutOpen ? "flyout-open" : ""}`}>
          <button
            type="button"
            className="sidebar-link sidebar-link-button"
            ref={anchorRef}
            aria-label={label}
            aria-expanded={!!isFlyoutOpen}
            aria-controls={isFlyoutOpen ? flyoutId : undefined}
            onClick={() => onFlyoutToggle && onFlyoutToggle()}
            uk-tooltip={label}
          >
            {leftContent}
          </button>

          {isFlyoutOpen && (
            <SidebarFlyout anchorRef={anchorRef} id={flyoutId} label={label} onClose={onFlyoutToggle}>
              {children.items}
            </SidebarFlyout>
          )}
        </li>
      );
    }

    return (
      <li className={`sidebar-item has-children ${className}`}>
        <button
          type="button"
          className="sidebar-link sidebar-link-button"
          onClick={() => onToggle && onToggle()}
        >
          {leftContent}

          <span className={`sidebar-chevron ${isOpen ? "open" : ""}`}>
            <FeatherIcon name="chevron-down" />
          </span>
        </button>

        {isOpen && <ul className="sidebar-subnav">{children.items}</ul>}
      </li>
    );
  }

  return (
    <li className={`sidebar-item ${className}`}>
      <Link
        to={to}
        className="sidebar-link"
        onClick={onClick}
        uk-tooltip={collapsed ? label : ""}
      >
        {leftContent}

        {!collapsed && badge ? (
          <Badge className="hms-notification-count" count={badge} />
        ) : null}
      </Link>
    </li>
  );
};

const SidebarSubItem = ({ to, label, onClick, flyout = false, moduleKey, className = "" }) => {
  const canShow = useCanShow();
  if (!canShow(moduleKey)) return null;
  return (
    <li className={flyout ? "sidebar-flyout-item" : "sidebar-subitem"}>
      <Link
        to={to}
        onClick={onClick}
        className={`${flyout ? "sidebar-flyout-link" : "sidebar-subnav-link"} ${className}`}
      >
        <span className={flyout ? "sidebar-flyout-label" : "sidebar-subnav-label"}>
          {label}
        </span>
      </Link>
    </li>
  );
};

const SidebarSubGroup = ({ flyout = false }) => (
  <li
    className={flyout ? "sidebar-flyout-group" : "sidebar-subnav-group"}
    aria-hidden="true"
  />
);

const Sidebar = () => {
  const { user: account, inbox, logout } = useContext(AppContexts.AuthContext);
  const { canView, isOwner, permsLoaded } = usePermissionsStore();
  const { newOrderCount, getNewOrderCount } = useContext(
    AppContexts.OrdersContext,
  );

  const location = useLocation();
  const [pinned, setPinned] = useState(() => localStorage.getItem("sidebar-pinned") === "true");
  const [collapsed, setCollapsed] = useState(() => !pinned);
  const [openMenu, setOpenMenu] = useState(null);
  const [flyoutMenu, setFlyoutMenu] = useState(null);
  const [mobileOpenMenu, setMobileOpenMenu] = useState(null);

    useEffect(() => {
        getNewOrderCount();
    }, []);

    useEffect(() => {
        if (!pinned) {
            setCollapsed(true);
        }
        const activeMenu = getActiveSidebarMenu(location.pathname);
        setOpenMenu(activeMenu);
        setFlyoutMenu(null);
        setMobileOpenMenu(activeMenu);
        const mobileMenu = document.getElementById('mobile-menu');
        if (mobileMenu) {
            UIkit.offcanvas(mobileMenu).hide();
        }
    }, [location.pathname]);

  const handleTogglePin = () => {
    const next = !pinned;
    setPinned(next);
    localStorage.setItem("sidebar-pinned", String(next));
    if (next) {
      setCollapsed(false);
    }
  };

  const handleLogout = async () => {
    logout()
      .then(() => {
        console.log("Goodbye");
      })
      .catch((err) => {
        console.warn("Error logging out:", err);
      });
  };

  const profileImage = useMemo(() => {
    return (
      (account?.user?.attachments || []).find((a) => a.type === "image") || null
    );
  }, [account]);

  const isActive = (paths, options) =>
    matchesSidebarPath(location.pathname, paths, options) ? "is-active" : "";

  const hasExtras = () => {
    return (account?.details?.product || "").split(",").includes("extras");
  };

  // Tasks is now GA for every account (see app-layout).
  const hasOpsTasks = true;
  const operationsActivePaths = [
    "/operations",
    "/operations/housekeeping",
    "/operations/inspection-board",
    "/operations/survey-board",
    "/operations/maintenance",
    "/operations/rules",
    "/operations/checklists",
    "/work-schedules",
    "/availability-calendar",
    "/operations/storage",
    ...(hasOpsTasks
      ? [
          "/operations/calendar",
          "/operations/team-scheduler",
          "/operations/tasks",
        ]
      : ["/work-orders"]),
    ...(hasOpsTasks && FEATURES.CLEANING_STANDARDS_ENABLED
      ? ["/operations/standards-structure"]
      : []),
    ...(hasOpsTasks && FEATURES.PROPERTY_SETUP_ENABLED
      ? ["/operations/property-setup"]
      : []),
  ];
  const myTasksPath = "/tasks";

  // Module keys that gate visibility of each collapsible group. A group is
  // shown when the user can view ANY of its modules.
  const MESSAGING_MODULES = [
    "messaging.scheduler",
    "messaging.extend_stay",
    "messaging.alerts",
    "messaging.surveys",
    "messaging.responses",
    "messaging.templates",
  ];
  const OPERATIONS_MODULES = [
    "operations.property_defaults",
    "operations.calendar",
    "operations.work_orders",
    "operations.tasks",
    "operations.inspections",
    "operations.surveys",
    "operations.inventory",
    "operations.housekeeping",
    "operations.checklists",
    "operations.standards",
    "operations.work_schedules",
  ];
  const hasOnlyMyTasksAccess = useMemo(() => {
    if (isOwner || !permsLoaded || !canView("operations.tasks")) {
      return false;
    }

    return [
      "operations.property_defaults",
    "operations.calendar",
      "operations.work_orders",
      "operations.inspections",
    "operations.surveys",
      "operations.inventory",
      "operations.housekeeping",
      "operations.checklists",
      "operations.standards",
      "operations.work_schedules",
    ].every((moduleKey) => !canView(moduleKey));
  }, [canView, isOwner, permsLoaded]);
  const GUIDES_MODULES = [
    "guides.master",
    "guides.home",
    "guides.extras",
    "guides.area",
    "admin.settings",
    "guides.payments",
  ];
  const CRM_MODULES = [
    "crm.leads",
    "crm.guest_leads",
    "crm.owner_leads",
    "crm.contacts",
    "crm.pipelines",
    "crm.activities",
    "crm.notes",
  ];

  const hasPrimaryItems = permsLoaded && (
    ["messaging.messages", "messaging.guests", "messaging.contacts", "admin.integrations"].some(canView) ||
    CRM_MODULES.some(canView) ||
    (hasExtras() && canView("guides.payments"))
  );
  const hasOperationsItems = permsLoaded && !hasOnlyMyTasksAccess && OPERATIONS_MODULES.some(canView);
  const hasGuideItems = permsLoaded && GUIDES_MODULES
    .filter((key) => hasExtras() || !["guides.extras", "guides.payments"].includes(key))
    .some(canView);
  const hasPersonalItems = permsLoaded && ["operations.tasks", "reports"].some(canView);
  // Keep links in the same functional area together for focused roles.
  const showPrimaryDivider = hasPrimaryItems && (hasOperationsItems || hasGuideItems || (hasPersonalItems && !CRM_MODULES.some(canView)));
  const showPersonalDivider = hasPersonalItems && hasGuideItems;
  const hasOperationsOverviewItems = permsLoaded && (
    canView("operations.calendar") ||
    canView(hasOpsTasks ? "operations.tasks" : "operations.work_orders") ||
    canView("operations.inventory") ||
    (FEATURES.INSPECTIONS_ENABLED && canView("operations.inspections")) || canView("operations.surveys")
  );
  const hasChecklistItems = permsLoaded && FEATURES.CHECKLISTS_ENABLED && canView("operations.checklists");
  const hasScheduleItems = permsLoaded && canView("operations.work_schedules");
  const hasOperationsSetupItems = permsLoaded && (
    canView("operations.property_defaults") || hasChecklistItems || hasScheduleItems
  );

  const renderOperationsItems = (flyout = false) => (
    <>
      <SidebarSubItem to="/operations" label="Dashboard" flyout={flyout} moduleKey="operations.calendar" className={isActive("/operations", { exact: true })} />
      {hasOpsTasks ? (
        <>
          <SidebarSubItem to="/operations/tasks" label="Tasks" flyout={flyout} moduleKey="operations.tasks" className={isActive("/operations/tasks")} />
          <SidebarSubItem to="/operations/calendar" label="Task Calendar" flyout={flyout} moduleKey="operations.calendar" className={isActive("/operations/calendar") || isActive("/operations/team-scheduler")} />
        </>
      ) : (
        <SidebarSubItem to="/work-orders" label="Work Orders" flyout={flyout} moduleKey="operations.work_orders" className={isActive("/work-orders")} />
      )}
      <SidebarSubItem to="/operations/storage" label="Inventory" flyout={flyout} moduleKey="operations.inventory" className={isActive("/operations/storage")} />
      {FEATURES.INSPECTIONS_ENABLED && (
        <SidebarSubItem to="/operations/inspection-board" label="Inspection Board" flyout={flyout} moduleKey="operations.inspections" className={isActive("/operations/inspection-board")} />
      )}
      <SidebarSubItem to="/operations/survey-board" label="Survey Board" flyout={flyout} moduleKey="operations.surveys" className={isActive("/operations/survey-board")} />
      {hasOperationsOverviewItems && hasOperationsSetupItems && <SidebarSubGroup flyout={flyout} />}
      <SidebarSubItem to="/operations/property-defaults" label="Property Defaults" flyout={flyout} moduleKey="operations.property_defaults" className={isActive("/operations/property-defaults")} />
      {FEATURES.CHECKLISTS_ENABLED && (
        <SidebarSubItem
          to="/operations/checklists"
          moduleKey="operations.checklists"
          label="Checklists"
          flyout={flyout}
          className={isActive("/operations/checklists")}
        />
      )}
      <SidebarSubItem to="/work-schedules" label="Work Schedules" flyout={flyout} moduleKey="operations.work_schedules" className={isActive("/work-schedules")} />
      <SidebarSubItem to="/availability-calendar" label="Availability Calendar" flyout={flyout} moduleKey="operations.work_schedules" className={isActive("/availability-calendar")} />
    </>
  );

  if (!account) return null;

  return (
    <>
      <div className={`rbr-sidebar ${collapsed ? "collapsed" : ""} ${pinned ? "pinned" : ""}`}>
        <div className="rbr-sidebar-top">
          <div className="rbr-sidebar-brand">
            <Link to={FEATURE_ROUTES.dashboard} className="rbr-sidebar-logo">
              <Image src="/images/ruebaur-logo.png" alt="RueBaRue" />
            </Link>

            <div className="rbr-sidebar-actions">
              {!collapsed && (
                <button
                  type="button"
                  className={`rbr-sidebar-pin ${pinned ? "is-pinned" : ""}`}
                  onClick={handleTogglePin}
                  title=""
                  uk-tooltip={`title: ${pinned ? "Unpin Sidebar" : "Pin Sidebar Open"}; pos: right; delay: 300`}
                >
                  <PinIcon active={pinned} />
                </button>
              )}
              <button
                type="button"
                className="rbr-sidebar-toggle"
                onClick={() => setCollapsed((prev) => !prev)}
                title={collapsed ? "Expand" : "Collapse"}
              >
                <FeatherIcon name={collapsed ? "menu" : "sidebar"} />
              </button>
            </div>
          </div>

          {!!account && account?.details?.type !== "onboarding" && (
            <ul className="sidebar-nav">
                <SidebarItem
                to={FEATURE_ROUTES.messages}
                label="Messages"
                icon="message-circle"
                className={isActive("/messages")}
                collapsed={collapsed}
                moduleKey="messaging.messages"
                />
                <SidebarItem
                to={FEATURE_ROUTES.guests}
                label="Guests"
                icon="users"
                className={isActive("/guests")}
                collapsed={collapsed}
                moduleKey="messaging.guests"
                />
                <SidebarItem
                to={FEATURE_ROUTES.contacts}
                label="Contacts"
                icon="user"
                className={isActive("/contacts")}
                collapsed={collapsed}
                moduleKey="messaging.contacts"
                />
                <SidebarItem
                to={FEATURE_ROUTES.crm}
                label="CRM"
                icon="briefcase"
                className={isActive("/crm")}
                collapsed={collapsed}
                requireAny={CRM_MODULES}
                />
                <SidebarItem
                to={FEATURE_ROUTES.locks}
                label="Smart Locks"
                icon="key"
                className={isActive("/smart-locks")}
                collapsed={collapsed}
                moduleKey="admin.integrations"
                />
                {hasExtras() && (
                <SidebarItem
                    to={FEATURE_ROUTES.orders}
                    label="Orders"
                    icon="shopping-cart"
                    className={isActive("/orders")}
                    collapsed={collapsed}
                    badge={+newOrderCount > 0 ? +newOrderCount : null}
                  moduleKey="guides.payments"
                />
                )}

                {showPrimaryDivider && <li className="sidebar-divider" />}

                <SidebarItem
                    label="Messaging"
                    icon="zap"
                    className={isActive(MESSAGING_PATHS)}
                    collapsed={collapsed}
                    requireAny={MESSAGING_MODULES}
                    isOpen={openMenu === "messaging"}
                    onToggle={() =>
                        setOpenMenu((prev) => (prev === "messaging" ? null : "messaging"))
                    }
                    isFlyoutOpen={flyoutMenu === "messaging"}
                    onFlyoutToggle={() => {
                        setFlyoutMenu((prev) =>
                          toggleSidebarMenu(prev, "messaging"),
                        );
                    }}
                    children={{
                        items: (
                        <>
                            <SidebarSubItem to="/scheduler" moduleKey="messaging.scheduler" label="Scheduler" flyout={collapsed} className={isActive("/scheduler")} />
                            <SidebarSubItem to="/extend-guest-stay" moduleKey="messaging.extend_stay" label="Extend Stays" flyout={collapsed} className={isActive("/extend-guest-stay")} />
                            <SidebarSubItem to="/alerts" moduleKey="messaging.alerts" label="Alerts" flyout={collapsed} className={isActive("/alerts")} />
                            <SidebarSubItem to="/surveys" moduleKey="messaging.surveys" label="Surveys" flyout={collapsed} className={isActive("/surveys")} />
                            <SidebarSubItem to="/saved-responses" moduleKey="messaging.responses" label="Saved Responses" flyout={collapsed} className={isActive("/saved-responses")} />
                            <SidebarSubItem to="/message-templates" moduleKey="messaging.templates" label="Message Templates" flyout={collapsed} className={isActive("/message-templates")} />
                        </>
                        ),
                    }}
                    />

            {!hasOnlyMyTasksAccess && (
                <SidebarItem
                    label="Operations"
                    requireAny={OPERATIONS_MODULES}
                    icon="sliders"
                    className={isActive(operationsActivePaths)}
                    collapsed={collapsed}
                    isOpen={openMenu === "operations"}
                    onToggle={() =>
                        setOpenMenu((prev) => (prev === "operations" ? null : "operations"))
                    }
                    isFlyoutOpen={flyoutMenu === "operations"}
                    onFlyoutToggle={() => {
                        setFlyoutMenu((prev) =>
                          toggleSidebarMenu(prev, "operations"),
                        );
                    }}
                    children={{
                      items: renderOperationsItems(collapsed),
                    }}
                    />
            )}

                <SidebarItem
                    label="Guest Guides"
                    icon="book"
                    className={isActive(GUEST_GUIDE_PATHS)}
                    collapsed={collapsed}
                    requireAny={GUIDES_MODULES}
                    isOpen={openMenu === "guides"}
                    onToggle={() =>
                        setOpenMenu((prev) => (prev === "guides" ? null : "guides"))
                    }
                    isFlyoutOpen={flyoutMenu === "guides"}
                    onFlyoutToggle={() => {
                        setFlyoutMenu((prev) =>
                          toggleSidebarMenu(prev, "guides"),
                        );
                    }}
                    children={{
                        items: (
                        <>
                            <SidebarSubItem
                            to={FEATURE_ROUTES.guides}
                            label="Guide Designer"
                            flyout={collapsed}
                            moduleKey="guides.master"
                            className={isActive("/guide-designer")}
                            />

                            <SidebarSubItem
                            to={FEATURE_ROUTES.homeGuides}
                            label="Home Guides"
                            flyout={collapsed}
                            moduleKey="guides.home"
                            className={isActive("/properties")}
                            />
                            {hasExtras() && (
                            <SidebarSubItem
                                to={FEATURE_ROUTES.extras}
                                label="Extras Guide"
                                flyout={collapsed}
                                moduleKey="guides.extras"
                                className={isActive("/extras")}
                            />
                            )}
                            <SidebarSubItem
                            to={FEATURE_ROUTES.areas}
                            label="Area Guides"
                            flyout={collapsed}
                            moduleKey="guides.area"
                            className={isActive(["/areas", "/areas_detail"])}
                            />
                            <SidebarSubItem
                            to={FEATURE_ROUTES.fields}
                            label="Property Field Values"
                            flyout={collapsed}
                            moduleKey="admin.settings"
                            className={isActive("/property-field-values")}
                            />
                            <SidebarSubItem
                            to={FEATURE_ROUTES.subscriptions}
                            label="Subscriptions"
                            flyout={collapsed}
                            moduleKey="guides.master"
                            className={isActive("/subscriptions")}
                            />
                        </>
                        ),
                    }}
                    />

                {showPersonalDivider && <li className="sidebar-divider" />}

                <SidebarItem
                to={myTasksPath}
                label="My Tasks"
                icon="check-square"
                className={isActive(myTasksPath)}
                collapsed={collapsed}
                moduleKey="operations.tasks"
                />

                <SidebarItem
                    to={FEATURE_ROUTES.reports}
                    label="Reports"
                    icon="bar-chart-2"
                    className={isActive("/reports")}
                    collapsed={collapsed}
                    moduleKey="reports"
                    />

                <SidebarItem
                    to={FEATURE_ROUTES.discovery}
                    label="Your Features"
                    icon="grid"
                    className={isActive("/features")}
                    collapsed={collapsed}
                />
                <SidebarItem
                    to={FEATURE_ROUTES.news}
                    label="What's New"
                    icon="bell"
                    className={isActive("/whats-new")}
                    collapsed={collapsed}
                />



            </ul>
            )}
        </div>

       {!!account && (
        <div className="rbr-sidebar-bottom">
          <div className="sidebar-account-info">
            <div className="sidebar-account-info-row">
              <span className="sidebar-account-info-label">Signed in as</span>
              <span className="sidebar-account-info-value" title={account?.user?.email}>{account?.user?.email}</span>
            </div>
            {inbox && inbox.phone && (
              <div className="sidebar-account-info-row">
                <span className="sidebar-account-info-label">SMS Number</span>
                <span className="sidebar-account-info-value">{inbox.phone}</span>
              </div>
            )}
          </div>
        </div>
        )}

      </div>

      <button
        id="sidebar-mobile-toggle"
        className="uk-hidden@xl rbr-mobile-toggle"
        uk-toggle="target: #mobile-menu; animation: uk-animation-fade"
        aria-expanded="false"
      >
        <span uk-navbar-toggle-icon=""></span>
      </button>

      <div id="mobile-menu" uk-offcanvas="overlay: true">
        <div className="uk-offcanvas-bar mobile-sidebar">
          <div className="mobile-sidebar-header">
            <Link to={FEATURE_ROUTES.dashboard} className="rbr-sidebar-logo">
              <Image src="/images/ruebaur-logo.png" alt="RueBaRue" />
            </Link>
            <button
              className="uk-offcanvas-close mobile-sidebar-close"
              type="button"
              uk-close
            >
              <Image src="/images/close-small.svg" alt="close" />
            </button>
          </div>

          {!!account && (
            <div className="mobile-menu-profile">
              <Link to={FEATURE_ROUTES.profile} className="mobile-menu-profile-link">
                <img
                  src={profileImage?.url || "/images/profile.svg"}
                  className="mobile-menu-avatar"
                  alt="Profile"
                />
                <div className="mobile-menu-profile-info">
                  <span className="mobile-menu-profile-name">
                    {account?.user?.first_name || account?.user?.email}
                  </span>
                  <span className="mobile-menu-profile-email">
                    {account?.user?.email}
                  </span>
                </div>
              </Link>
            </div>
          )}

          {!!account && account?.details?.type !== "onboarding" && (
          <ul className="sidebar-nav">
            <SidebarItem to="/messages" label="Messages" icon="message-circle" className={isActive("/messages")} collapsed={false} moduleKey="messaging.messages" />
            <SidebarItem to="/guests" label="Guests" icon="users" className={isActive("/guests")} collapsed={false} moduleKey="messaging.guests" />
            <SidebarItem to="/contacts" label="Contacts" icon="user" className={isActive("/contacts")} collapsed={false} moduleKey="messaging.contacts" />
              <SidebarItem to="/crm" label="CRM" icon="briefcase" className={isActive("/crm")} collapsed={false} requireAny={CRM_MODULES} />
            <SidebarItem to="/smart-locks" label="Smart Locks" icon="key" className={isActive("/smart-locks")} collapsed={false} moduleKey="admin.integrations" />
            {hasExtras() && (
              <SidebarItem to="/orders" label="Orders" icon="shopping-cart" className={isActive("/orders")} collapsed={false} badge={+newOrderCount > 0 ? +newOrderCount : null} moduleKey="guides.payments" />
            )}

            {showPrimaryDivider && <li className="sidebar-divider" />}

            <SidebarItem
              label="Messaging"
              icon="zap"
              className={isActive(MESSAGING_PATHS)}
              collapsed={false}
              requireAny={MESSAGING_MODULES}
              isOpen={mobileOpenMenu === "messaging"}
              onToggle={() => setMobileOpenMenu((prev) => (prev === "messaging" ? null : "messaging"))}
              children={{
                items: (
                  <>
                    <SidebarSubItem to="/scheduler" moduleKey="messaging.scheduler" label="Scheduler" className={isActive("/scheduler")} />
                    <SidebarSubItem to="/extend-guest-stay" moduleKey="messaging.extend_stay" label="Extend Stays" className={isActive("/extend-guest-stay")} />
                    <SidebarSubItem to="/alerts" moduleKey="messaging.alerts" label="Alerts" className={isActive("/alerts")} />
                    <SidebarSubItem to="/surveys" moduleKey="messaging.surveys" label="Surveys" className={isActive("/surveys")} />
                    <SidebarSubItem to="/saved-responses" moduleKey="messaging.responses" label="Saved Responses" className={isActive("/saved-responses")} />
                    <SidebarSubItem to="/message-templates" moduleKey="messaging.templates" label="Message Templates" className={isActive("/message-templates")} />
                  </>
                ),
              }}
            />

            {!hasOnlyMyTasksAccess && (
            <SidebarItem
              label="Operations"
                    requireAny={OPERATIONS_MODULES}
              icon="sliders"
              className={isActive(operationsActivePaths)}
              collapsed={false}
              isOpen={mobileOpenMenu === "operations"}
              onToggle={() => setMobileOpenMenu((prev) => (prev === "operations" ? null : "operations"))}
              children={{
                items: renderOperationsItems(false),
              }}
            />
            )}

            <SidebarItem
              label="Guest Guides"
              icon="book"
              className={isActive(GUEST_GUIDE_PATHS)}
              collapsed={false}
              requireAny={GUIDES_MODULES}
              isOpen={mobileOpenMenu === "guides"}
              onToggle={() => setMobileOpenMenu((prev) => (prev === "guides" ? null : "guides"))}
              children={{
                items: (
                  <>
                    <SidebarSubItem to="/guide-designer" moduleKey="guides.master" label="Guide Designer" className={isActive("/guide-designer")} />
                    <SidebarSubItem to="/properties" moduleKey="guides.home" label="Home Guides" className={isActive("/properties")} />
                    <SidebarSubItem to="/areas" moduleKey="guides.area" label="Area Guides" className={isActive(["/areas", "/areas_detail"])} />
                    {hasExtras() && <SidebarSubItem to="/extras" moduleKey="guides.extras" label="Extras Guide" className={isActive("/extras")} />}
                    <SidebarSubItem to="/property-field-values" moduleKey="admin.settings" label="Property Field Values" className={isActive("/property-field-values")} />
                    <SidebarSubItem to="/subscriptions" moduleKey="guides.master" label="Subscriptions" className={isActive("/subscriptions")} />
                  </>
                ),
              }}
            />

            {showPersonalDivider && <li className="sidebar-divider" />}

            <SidebarItem to={myTasksPath} label="My Tasks" icon="check-square" className={isActive(myTasksPath)} collapsed={false} moduleKey="operations.tasks" />
            <SidebarItem to="/reports" label="Reports" icon="bar-chart-2" className={isActive("/reports")} collapsed={false} moduleKey="reports" />

            <li className="sidebar-divider"></li>

            <SidebarItem to={FEATURE_ROUTES.profile} label="Profile" icon="user" className={isActive("/profile")} collapsed={false} />
            <SidebarItem to={FEATURE_ROUTES.discovery} label="Your Features" icon="grid" className={isActive("/features")} collapsed={false} />
            <SidebarItem to={FEATURE_ROUTES.news} label="What's New" icon="bell" className={isActive("/whats-new")} collapsed={false} />
            <SidebarItem
              to="/help"
              label="Help Center"
              icon="info"
              collapsed={false}
            />
            <SidebarItem to="#" label="Sign Out" icon="log-out" collapsed={false} onClick={handleLogout} />
          </ul>
          )}

          <div className="sidebar-account-info" style={{ padding: '16px 12px', borderTop: '1px solid #e5e7eb', marginTop: 'auto' }}>
            <div className="sidebar-account-info-row">
              <span className="sidebar-account-info-label">Signed in as</span>
              <span className="sidebar-account-info-value">{account?.user?.email}</span>
            </div>
            {inbox && inbox.phone && (
              <div className="sidebar-account-info-row">
                <span className="sidebar-account-info-label">SMS Number</span>
                <span className="sidebar-account-info-value">{inbox.phone}</span>
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default Sidebar;
